import { ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useRestaurantStore } from '@/stores/restaurant'
import { useActiveReservation } from '@/composables/features/useActiveReservation'
import { authApi } from '@/api/auth'
import { reservationApi } from '@/api/reservation'
import { useOrder } from './useOrder'
import type { User } from '@/interfaces'
import { parseApiError } from '@/utils/errorHandler'

/** Build a clean query from the current route, stripping 'drawer' so a lingering
 *  drawer=order param is never re-introduced during reservation/order URL updates. */
function cleanQuery(
  query: Record<string, unknown>,
  extra: Record<string, string>
): Record<string, string> {
  const result = { ...(query as Record<string, string>), ...extra }
  delete result.drawer
  return result
}

function isTableAvailableFromResponse(response: unknown): boolean {
  if (response === true || response === 'true') return true
  if (response && typeof response === 'object') {
    const obj = response as Record<string, unknown>
    if (obj.error) return false
    const raw = obj.data ?? obj.available ?? obj.table_available
    if (raw === true || raw === 'true') return true
    if ('show_table_available' in obj) return obj.show_table_available === false
  }
  return false
}

function getReservationIdFromValidityResponse(response: unknown): string | null {
  if (!response || typeof response !== 'object') return null
  const data = (response as Record<string, unknown>).data ?? response
  const obj = data as Record<string, unknown>
  const id =
    obj.active_reservation_id ??
    (obj.active_reservation as Record<string, unknown>)?.active_reservation_id ??
    (obj.active_reservation as Record<string, unknown>)?.id ??
    obj.id
  if (id != null) return String(id)
  return null
}

export function useUnifiedOrderFlow(tenantId: string, tableNo: string) {
  const router = useRouter()
  const route = useRoute()
  const authStore = useAuthStore()
  const restaurantStore = useRestaurantStore()
  const { getStoredActiveReservation } = useActiveReservation()
  const { placeOrder, cart } = useOrder()

  const loading = ref<boolean>(false)
  const step = ref<'mobile' | 'otp' | 'register' | 'complete'>('mobile')
  const accountExists = ref<boolean | null>(null)

  /**
   * Send OTP: existing account → step 'otp'; new user (422) → send OTP with account_exists 'No', step 'otp'.
   * New users verify OTP in verifyUnifiedOtp, then we check table and call make_instant_reservation.
   */
  const sendUnifiedOtp = async (formattedMobile: string): Promise<void> => {
    loading.value = true
    try {
      try {
        const checkResponse = await authApi.checkCustomerMobile({
          mobile_no: formattedMobile,
        })
        if (checkResponse?.error) {
          throw new Error(parseApiError(checkResponse).message)
        }
        accountExists.value = true
        step.value = 'otp'
        return
      } catch (checkError: unknown) {
        const ax = checkError as { response?: { status?: number; data?: unknown } }
        if (ax.response?.status === 422) {
          await authStore.sendOTP({
            mobile_no: formattedMobile,
            account_exists: 'No',
          })
          accountExists.value = false
          step.value = 'otp'
          return
        }
        throw checkError
      }
    } catch (error: unknown) {
      throw new Error(parseApiError(error).message)
    } finally {
      loading.value = false
    }
  }

  const verifyUnifiedOtp = async (
    formattedMobile: string,
    otpCode: string
  ): Promise<{
    success: boolean
    needsRegistration: boolean
    reservationId?: string
    instantReservationDone?: boolean
  }> => {
    loading.value = true

    try {
      const otpCheckResponse = await authApi.checkOtpValidity({
        mobile_no: formattedMobile,
        otp: otpCode,
      })

      if (otpCheckResponse.error) {
        throw new Error(parseApiError(otpCheckResponse).message)
      }

      if (accountExists.value === true) {
        await authStore.verifyOTP({
          mobile_no: formattedMobile,
          otp: otpCode,
        })
      } else if (accountExists.value === false) {
        // New user: OTP is valid — show registration form to collect name/email,
        // matching the MenuItemCustomizeDrawer flow. completeRegistrationAndOrder
        // will call checkCustomerInfoValidity + makeInstantRegisteredReservation.
        step.value = 'register'
        return { success: false, needsRegistration: true }
      }

      let reservationId: string | null = null
      if (tableNo && authStore.isAuthenticated && authStore.user?.mobile_no) {
        const stored = getStoredActiveReservation()
        const reservationNumber = stored?.reservationNumber

        if (reservationNumber?.trim()) {
          const validityRes = await reservationApi.checkReservationNumberValidity({
            reservation_number: reservationNumber.trim(),
            tenant_id: tenantId,
            table_no: tableNo,
          })
          if (!validityRes.error) {
            const id = getReservationIdFromValidityResponse(validityRes)
            if (id) {
              reservationId = id
              const data = ((validityRes as Record<string, unknown>).data ?? validityRes) as Record<
                string,
                unknown
              >
              if (data?.active_reservation && data?.user_data) {
                authStore.setAuthFromReservationValidation({
                  active_reservation: data.active_reservation as {
                    customer_access_token: string
                    active_reservation_id: number
                    tenant_id: string
                    table_no: string
                    reservation_numer?: string
                    reservation_number?: string
                  },
                  user_data: data.user_data as User,
                })
              } else if (data?.customer_access_token && data?.user_data) {
                authStore.setTokens(data.customer_access_token as string, data.user_data as User)
                authStore.setActiveReservation(id, { tenantId, tableNo })
              }
              router.replace({
                path: route.path,
                query: cleanQuery(route.query, { reservation_id: id }),
              })
            }
          }
        }

        if (!reservationId) {
          const availabilityRes = await restaurantStore.checkTableAvailability({
            tenant_id: tenantId,
            table_no: tableNo,
          })
          if (!isTableAvailableFromResponse(availabilityRes)) {
            throw new Error('TABLE_UNAVAILABLE')
          }
          const reservationResponse = await reservationApi.makeInstantRegisteredReservation({
            mobile_no: authStore.user.mobile_no,
            tenant_id: tenantId,
            table_no: tableNo,
          })
          if (reservationResponse.error) {
            throw new Error(parseApiError(reservationResponse).message)
          }
          const reservation = reservationResponse.data as unknown as Record<string, unknown>
          reservationId =
            (reservation?.active_reservation_id != null
              ? String(reservation.active_reservation_id)
              : null) ?? (reservation?.id != null ? String(reservation.id) : null)
          if (reservationId) {
            router.replace({
              path: route.path,
              query: cleanQuery(route.query, { reservation_id: reservationId }),
            })
          }
        }
      }

      if (reservationId && cart.value.items.length > 0) {
        const orderSuccess = await placeOrder(reservationId)
        if (!orderSuccess) {
          throw new Error('Failed to place order')
        }
      }

      step.value = 'complete'
      return {
        success: true,
        needsRegistration: false,
        ...(reservationId ? { reservationId } : {}),
      }
    } catch (error: unknown) {
      throw new Error(parseApiError(error).message)
    } finally {
      loading.value = false
    }
  }

  const completeRegistrationAndOrder = async (
    formattedMobile: string,
    registrationData: {
      first_name: string
      last_name: string
      email?: string
    }
  ): Promise<{ success: boolean; reservationId?: string }> => {
    loading.value = true

    try {
      await authStore.checkCustomerInfoValidity({
        mobile_no: formattedMobile,
        ...registrationData,
      })

      // FIX 1: use formattedMobile as fallback — authStore.user?.mobile_no may be null
      // immediately after checkCustomerInfoValidity if user_data doesn't include mobile_no.
      const mobileNo = authStore.user?.mobile_no ?? formattedMobile

      let reservationId: string | null = null
      if (tableNo && authStore.isAuthenticated && mobileNo) {
        const stored = getStoredActiveReservation()
        const reservationNumber = stored?.reservationNumber

        if (reservationNumber?.trim()) {
          const validityRes = await reservationApi.checkReservationNumberValidity({
            reservation_number: reservationNumber.trim(),
            tenant_id: tenantId,
            table_no: tableNo,
          })
          if (!validityRes.error) {
            const id = getReservationIdFromValidityResponse(validityRes)
            if (id) {
              reservationId = id
              const data = ((validityRes as Record<string, unknown>).data ?? validityRes) as Record<
                string,
                unknown
              >
              if (data?.active_reservation && data?.user_data) {
                authStore.setAuthFromReservationValidation({
                  active_reservation: data.active_reservation as {
                    customer_access_token: string
                    active_reservation_id: number
                    tenant_id: string
                    table_no: string
                    reservation_numer?: string
                    reservation_number?: string
                  },
                  user_data: data.user_data as User,
                })
              } else if (data?.customer_access_token && data?.user_data) {
                authStore.setTokens(data.customer_access_token as string, data.user_data as User)
                authStore.setActiveReservation(id, { tenantId, tableNo })
              }
              router.replace({
                path: route.path,
                query: cleanQuery(route.query, { reservation_id: id }),
              })
            }
          }
        }

        if (!reservationId) {
          const availabilityRes = await restaurantStore.checkTableAvailability({
            tenant_id: tenantId,
            table_no: tableNo,
          })
          if (!isTableAvailableFromResponse(availabilityRes)) {
            throw new Error('TABLE_UNAVAILABLE')
          }
          const reservationResponse = await reservationApi.makeInstantRegisteredReservation({
            mobile_no: mobileNo, // FIX 1: uses fallback-safe mobileNo
            tenant_id: tenantId,
            table_no: tableNo,
          })
          if (reservationResponse.error) {
            throw new Error(parseApiError(reservationResponse).message)
          }
          const reservation = reservationResponse.data as unknown as Record<string, unknown>
          reservationId =
            (reservation?.active_reservation_id != null
              ? String(reservation.active_reservation_id)
              : null) ?? (reservation?.id != null ? String(reservation.id) : null)
          if (reservationId) {
            // FIX 3: persist reservation in auth store immediately (mirrors MenuItemCustomizeDrawer)
            authStore.setActiveReservation(reservationId, { tenantId, tableNo })
            // FIX 2: use cleanQuery to strip drawer=order, preventing the cart from reopening
            router.replace({
              path: route.path,
              query: cleanQuery(route.query, { reservation_id: reservationId }),
            })
          }
        }
      }

      if (reservationId && cart.value.items.length > 0) {
        const orderSuccess = await placeOrder(reservationId)
        if (!orderSuccess) {
          throw new Error('Failed to place order')
        }
      }

      step.value = 'complete'
      return {
        success: true,
        ...(reservationId ? { reservationId } : {}),
      }
    } catch (error: unknown) {
      throw new Error(parseApiError(error).message)
    } finally {
      loading.value = false
    }
  }

  return {
    loading,
    step,
    accountExists,
    sendUnifiedOtp,
    verifyUnifiedOtp,
    completeRegistrationAndOrder,
  }
}
